/* eslint-disable react/display-name */
import React, { createContext, useContext, useState, ReactNode } from "react"
import { Snackbar, Alert, AlertColor } from "@mui/material"
import { useResponsive } from "../utils/useResponsive"
import { appBarHeightPixels } from "./AppProviders"

export interface SnackbarProps {
  snackbarShowMessage: (message: string, severity?: AlertColor) => void
}

const SnackbarContext = createContext<SnackbarProps | undefined>(undefined)

export const useSnackbar = (): SnackbarProps => {
  const context = useContext(SnackbarContext)
  if (!context) throw new Error("useSnackbar must be used within a SnackbarProvider")
  return context
}

interface SnackbarProviderProps {
  children: ReactNode
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [severity, setSeverity] = useState<AlertColor>("success")
  const { isMobile } = useResponsive()

  const snackbarShowMessage = (message: string, severity: AlertColor = "success") => {
    setMessage(message)
    setSeverity(severity)
    setOpen(true)
  }

  const handleClose = (event: any, reason: any) => {
    if (reason === "clickaway") return
    setOpen(false)
  }

  return (
    <SnackbarContext.Provider value={{ snackbarShowMessage }}>
      {children}
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: isMobile ? "center" : "right"
        }}
        autoHideDuration={5000}
        open={open}
        onClose={handleClose}
        sx={{
          "&.MuiSnackbar-anchorOriginTopCenter": { top: `${appBarHeightPixels + 8}px` },
          "& .MuiSnackbarContent-root": { mx: { xs: 2, md: undefined } }
        }}
      >
        <Alert variant={severity === "success" ? "standard" : "filled"} severity={severity} sx={{ width: { xs: "100%", md: "auto" }, whiteSpace: "pre-line" }}>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}
